import { TextField, Button } from '@mui/material';

import MyDialog from "~/mui/MyDialog";
import { checkForErrorsState, getProjectsAndKeysLink } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { apiCreateProjectWithKey, apiGetProjectSuggestName } from '~/providers/api';
import { IClientProject, IClientCreateProject } from "~/providers/client-interfaces";
import ExternalLink from "~/components/ExternalLink";
import { LICENCE_AGGR, MAX_USER_DESCIPTION_LEN } from "~/const";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import MyCheckbox from "~/mui/MyCheckbox";
import WhatAbout from "~/components/WhatAbout";

import "./style.less";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
	licenceAggr: boolean;
	internalName: string;
	setInternalName: boolean;
}

// chyby formulare
type TStateProjectErrors = {[key in keyof Pick<IClientCreateProject, "userTitle" | "userDescription" | "internalName">]: string; };

interface IClientCreateProjectModal {
	onSave?: (project: IClientProject) => void;
	onClose?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function ClientCreateProjectModal({
	onSave = () => {},
	onClose = () => {},
	onChange = () => {},
}: IClientCreateProjectModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
		licenceAggr: false,
		internalName: "",
		setInternalName: false,
	});
	const { state: stateProject, updateState: updateStateProject } = myUseState<IClientCreateProject>({
		internalName: "",
		userDescription: "",
		userTitle: "",
	});
	const { state: stateProjectError, setState: setStateProjectError } = myUseState<TStateProjectErrors>({
		userTitle: "",
		internalName: "",
		userDescription: "",
	});

	function getWasChanged(newState: Partial<IClientCreateProject>): boolean {
		const newStateObj = {
			...stateProject,
			...newState,
		};

		return newStateObj.internalName !== ""
			|| newStateObj.userDescription !== ""
			|| newStateObj.userTitle !== "";
	}

	function updateStateProjectWithChange(newState: Partial<IClientCreateProject>) {
		const wasChanged = getWasChanged(newState);

		updateStateProject(newState);
		updateState({
			wasChanged,
		});
		onChange(wasChanged);
	}

	function changeInternalName() {
		updateStateProject({
			internalName: state.internalName,
		});
		updateState({
			internalName: "",
			setInternalName: true,
		});
	}

	async function checkProjectId(userTitle: string) {
		const len = userTitle.length;

		/* eslint-disable-next-line */
		if (len < 2 || len > 50 || state.setInternalName) {
			return;
		}

		const suggestData = await apiGetProjectSuggestName(userTitle, lang);
		const internalName = suggestData.noErrors ? suggestData.data.internalName : "";

		updateState({
			internalName,
		});
		updateStateProject({
			internalName,
		});
	}

	function updateUserTitle(userTitle: string) {
		updateStateProjectWithChange({ userTitle });
		checkProjectId(userTitle);
	}

	async function dialogOnSave() {
		const errorData = checkForErrorsState<IClientCreateProject, TStateProjectErrors>(stateProject, {
			userTitle: { check: "string-2-35", msg: trans("createProject.error.userTitle") },
			userDescription: { check: "string-user-desc", msg: trans("createProject.error.userDescriptionCount", { count: MAX_USER_DESCIPTION_LEN }) },
			...state.setInternalName
				? {
					internalName: [
						{ check: "string-2-35", msg: trans("createProject.error.internalIdMin2") },
						{ check: "internal-id", msg: trans("createProject.error.internalNameCustom") },
					],
				}
				: {},
		});

		setStateProjectError(errorData.errorsState);

		// jsou tam chyby
		if (errorData.errorsCount > 0) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const createData = {
			...stateProject,
		};

		if (!state.setInternalName) {
			delete createData.internalName;
		}

		const outputData = await apiCreateProjectWithKey("client", createData, lang);

		if (outputData.noErrors) {
			onSave(outputData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("createProject.titleNew")} onClose={onClose} onSave={dialogOnSave} className="createProjectModal" dialogLoading={state.dialogLoading} disableSave={!state.wasChanged || !state.licenceAggr}
		errorMsg={state.errorMsg} saveCaption={trans("createProject")} confirmClose={state.wasChanged}>
		<div className="createProjectModal__whatAbout">
			<WhatAbout title={trans("tariffWhatAbout.projectTitle")} link={getProjectsAndKeysLink(lang)} />
		</div>
		<TextField
			label={trans("createProject.userTitle")}
			value={stateProject.userTitle}
			style={{ width: "100%" }}
			onChange={event => updateUserTitle(event.target.value)}
			color="success"
			autoComplete="off"
			className={state.internalName.length === 0 && !state.setInternalName ? "margin-bottom-large" : ""}
			error={stateProjectError.userTitle !== ""}
			helperText={stateProjectError.userTitle}
			size="small"
		/>
		{ state.internalName.length > 0 && <div className="createProjectModal__generateId">
			<span>
				{ trans("createProject.generatedId", { id: state.internalName }) }
			</span>
			<Button variant="outlined" onClick={changeInternalName}>
				{ trans("createProject.changeId") }
			</Button>
		</div> }
		{ state.setInternalName && <TextField
			label={trans("createProject.internalName")}
			value={stateProject.internalName}
			style={{ width: "100%" }}
			onChange={event => updateStateProjectWithChange({ internalName: event.target.value })}
			color="success"
			autoComplete="off"
			sx={{ margin: "var(--margin-large) 0" }}
			error={stateProjectError.internalName !== ""}
			helperText={stateProjectError.internalName}
			size="small"
		/> }
		<TextField
			label={trans("createProject.userDescription")}
			value={stateProject.userDescription}
			style={{ width: "100%" }}
			multiline={true}
			onChange={event => updateStateProjectWithChange({ userDescription: event.target.value })}
			color="success"
			autoComplete="off"
			rows={3}
			sx={{ marginBottom: "var(--margin-large)" }}
			error={stateProjectError.userDescription !== ""}
			helperText={stateProjectError.userDescription}
		/>
		<div className="createProjectModal__licenceCont">
			<MyCheckbox checked={state.licenceAggr} onChange={checked => updateState({ licenceAggr: checked })} label={<>
				{ trans("createKey.licenceMsg") }
				&nbsp;
				<ExternalLink to={LICENCE_AGGR[lang]} sx={{ fontSize: "var(--default-font-size)" }}>
					{ trans("createKey.licenceTitle") }
				</ExternalLink>
			</>} className="createProjectModal__licenceSwitch" />
		</div>
	</MyDialog>;
}
